<template>
    <div>
        <progress-header />

        <div class="d-flex justify-content-center flex-column flex-wrap pt-4 text-center" v-if="isLoading">
            <div>
                <div class="spinner-border" role="status">
                    <span class="sr-only">{{ $t('loading') }}...</span>
                </div>
            </div>
            <h4 class="mt-5" v-if="!this.queryResolved && this.longWaitInfo" v-html="this.longWaitInfo" />
        </div>
        <template v-else>
            <div class="container mt-4">
                <div class="row">
                    <div class="col-12 col-lg-9 mb-2">
                        <div class="card booking-form-card shadow-sm">
                            <div class="card-header">
                                <span class="icon position-pin-icon" />

                                {{ searchData.end_place_name }}
                                <font-awesome-icon icon="long-arrow-alt-left" class="in-text-icon" />
                                {{ searchData.start_place_name }}
                            </div>
                            <div class="w-100 p-3 font-weight-bold text-lg section-header green">
                                {{ $t('airport_transfers.pages.lowest_price') }}
                            </div>

                            <div
                                class="row search-result align-items-center py-4"
                                :class="{'border-top': index > 0}"
                                v-for="(travel, index) in returnsPriceOfDay"
                                :key="travel.id"
                            >
                                <div class="col-12 col-lg-7">
                                    <route-line class="d-inline-block" />

                                    <div class="stop-name d-inline-block">
                                        <stop
                                            :selected-stop="travel.selectedDepartureStop"
                                            :selected-stop-time="travel.selectedDepartureStop.stopTime"
                                            :place-name="searchData.end_place_name"
                                            :stops="travel.departureStops"
                                            :travel="travel"
                                            :date="searchData.return_date"
                                            type="departure"
                                            @input="selectStop($event)"
                                        />

                                        <stop
                                            :selected-stop="travel.selectedArrivalStop"
                                            :selected-stop-time="travel.selectedArrivalStop.stopTime"
                                            :place-name="searchData.start_place_name"
                                            :stops="travel.arrivalStops"
                                            :travel="travel"
                                            type="arrival"
                                            @input="selectStop($event)"
                                        />
                                    </div>
                                </div>

                                <div class="col-12 col-lg-2 text-right">
                                    <div class="text-lg text-blue-600 font-weight-bold">
                                        {{ !travel.totalCost ? '...' : formatPrice(travel.totalCost, currency) }}
                                    </div>
                                </div>

                                <div class="col-12 col-lg-3">
                                    <button class="btn btn-primary btn-sm w-100 select-button"
                                        :disabled="!travel.totalCost"
                                        @click="handlePick(travel, index, 'returnsPriceOfDay')"
                                    >
                                        {{ $t('airport_transfers.pages.select') }}
                                    </button>
                                </div>
                            </div>
                            <template v-if="returnsAll.length > 0">
                                <div class="w-100 p-3 font-weight-bold text-lg section-header green">
                                    {{ $t('airport_transfers.pages.all_transfers') }}
                                </div>
                            </template>
                            <div
                                class="d-flex align-items-center py-4"
                                :class="{'border-top': index > 0}"
                                v-for="(travel, index) in returnsAll"
                                :key="travel.id"
                            >
                                <div class="col-12 col-md-8">
                                    <route-line class="d-inline-block" />

                                    <div class="stop-name d-inline-block">
                                        <stop
                                            :selected-stop="travel.selectedDepartureStop"
                                            :selected-stop-time="travel.selectedDepartureStop.stopTime"
                                            :place-name="searchData.end_place_name"
                                            :stops="travel.departureStops"
                                            :travel="travel"
                                            :date="searchData.return_date"
                                            type="departure"
                                            @input="selectStop($event)"
                                        />

                                        <stop
                                            :selected-stop="travel.selectedArrivalStop"
                                            :selected-stop-time="travel.selectedArrivalStop.stopTime"
                                            :place-name="searchData.start_place_name"
                                            :stops="travel.arrivalStops"
                                            :travel="travel"
                                            type="arrival"
                                            @input="selectStop($event)"
                                        />
                                    </div>
                                </div>

                                <div class="col-12 col-md-4">
                                    <button
                                        class="btn btn-primary btn-sm w-100 select-button"
                                        @click="handlePick(travel, index, 'allPrices')"
                                    >
                                        {{ $t('airport_transfers.pages.select') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-lg-3">
                        <the-cart />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import TheCart from './components/TheCart';
    import i18n from '../../i18n';
    import ProgressHeader from './components/ProgressHeader';
    import Stop from './components/Stop';
    import RouteLine from '@components/RouteLine';
    import {AIRPORT_TRANSFERS_LONG_WAIT_MESSAGE_TIMEOUT} from '@/constants';
    import backofficeRepository from '@repositories/BackofficeRepository';
    import moment from 'moment/moment';

    export default {
        components: {
            TheCart,
            ProgressHeader,
            Stop,
            RouteLine,
        },
        data() {
            return {
                isLoading: true,
                returnsPriceOfDay: null,
                returnsAll: null,
                queryResolved: false,
                longWaitInfo: null,
            };
        },
        computed: {
            ...mapGetters({
                returnResults: 'airportTransfersSearch/getReturnResults',
                searchData: 'airportTransfersSearch/getSearchParameters',
                departureTravel: 'airportTransfersSearch/getDepartureTravel',
                sessionId: 'airportTransfersSearch/getSessionID',
                currency: 'airportTransfersSearch/getCurrency',
            }),
        },
        created() {
            let params = this.searchData;
            params = {
                ...params,
                departure_id: this.departureTravel.id,
                session_id: this.sessionId,
            };

            this.scheduleLongWaitMessage();

            if (!this.returnResults) {
                this.returnsPriceOfDay = [];
                this.returnsAll = [];
                this.isLoading = false;
            } else {
                this.selectFirstStop(this.returnResults);

                //load prices for each this.returnResults to totalCost prop via backofficeRepository.findLineRides
                const promises = [];
                this.returnResults.forEach((departure) => {
                    const promise = backofficeRepository.findLineRides(departure.selectedDepartureStop, departure.selectedArrivalStop, this.searchData.return_date + ' 00:00', this.currency, null, this.searchData.passengers_number).then((lineRides) => {
                        const thisLineRide = lineRides.filter(r => r.lineId == departure.id)[0];
                        departure.totalCost = thisLineRide.price.grossPrice*100;
                        //get time part only from timestamp thisLineRide.startDate
                        departure.selectedDepartureStop.stopTime = moment(thisLineRide.startDateFormatted).format('HH:mm');
                        departure.selectedArrivalStop.stopTime = moment(thisLineRide.endDateFormatted).format('HH:mm');
                        departure.lineRide = thisLineRide;
                    });
                    promises.push(promise);
                });

                Promise.all(promises).then(() => {
                    //sort by totalCost
                    this.returnResults.sort((a, b) => a.totalCost - b.totalCost);

                    this.returnsPriceOfDay = this.sortStops(this.returnResults.slice(0, 3));
                    this.returnsAll = this.sortStops(this.returnResults.slice(3));
                    //sort by departure times
                    this.returnsPriceOfDay = this.returnsPriceOfDay.sort((a, b) => parseInt(a.selectedDepartureStop.stopTime.replace(':', ''), 10) - parseInt(b.selectedDepartureStop.stopTime.replace(':', ''), 10));
                    if(this.returnsAll){
                        this.returnsAll = this.returnsAll.sort((a, b) => parseInt(a.selectedDepartureStop.stopTime.replace(':', ''), 10) - parseInt(b.selectedDepartureStop.stopTime.replace(':', ''), 10));
                    }
                    this.isLoading = false;
                });
            }
        },
        methods: {
            scheduleLongWaitMessage() {
                if(false === AIRPORT_TRANSFERS_LONG_WAIT_MESSAGE_TIMEOUT){
                    return;
                }
                this.queryResolved = false;
                setTimeout(function(){
                    if(!this.queryResolved){
                        this.longWaitInfo = i18n.tc('maintenance_long_wait_info');
                    }
                }.bind(this), AIRPORT_TRANSFERS_LONG_WAIT_MESSAGE_TIMEOUT);
            },

            setQueryResolved() {
                this.queryResolved = true;
                this.longWaitInfo = null;
            },

            sortStops(stops) {
                return stops.sort(
                    function(a, b) {
                        let first = parseInt(a.selectedDepartureStop.stopTime.replace(':', ''), 10);
                        let second = parseInt(b.selectedDepartureStop.stopTime.replace(':', ''), 10);

                        if (first === second) {
                            return 0;
                        }

                        return first <= second ? -1 : 1;
                    }
                );
            },

            handlePick(travel) {
                // trackGA('add_to_cart', {items: [{id: 'return', name: travel.name + ' - '+travel.id} ]});

                travel = {
                    ...travel,
                    ids: {
                        id: travel.id,
                        departureStopId: travel.selectedDepartureStop.id,
                        arrivalStopId: travel.selectedArrivalStop.id,
                    },
                    times: {
                        departureTime: travel.selectedDepartureStop.stopTime,
                        arrivalTime: travel.selectedArrivalStop.stopTime,
                    },
                };

                this.$store.dispatch('airportTransfersSearch/setReturnTravel', travel).then(() => {
                    this.$store.dispatch('airportTransfersSearch/setIsReturnsComplete', true).then(() => {
                        this.$router.push({ name: 'airport-transfers.passengers' });
                    });
                });
            },

            async selectStop(data) {
                data.travel.totalCost = null;

                if (data.type === 'departure') {
                    data.travel.selectedDepartureStop = data.stop;
                    data.travel.selectedDepartureStop.stopTime = null;
                } else {
                    data.travel.selectedArrivalStop = data.stop;
                    data.travel.selectedArrivalStop.stopTime = null;
                }

                //reload data rows
                this.returnsPriceOfDay = [...this.returnsPriceOfDay];
                this.returnsAll = [...this.returnsAll];

                try {
                    const lineRides = await backofficeRepository.findLineRides(
                        data.travel.selectedDepartureStop,
                        data.travel.selectedArrivalStop,
                        this.searchData.return_date + ' 00:00',
                        this.currency,
                        null,
                        this.searchData.passengers_number
                    );

                    const thisLineRide = lineRides.filter(r => r.lineId == data.travel.id)[0];
                    if (data.type === 'departure') {
                        data.travel.selectedDepartureStop.stopTime = moment(thisLineRide.startDateFormatted).format('HH:mm');
                    } else {
                        data.travel.selectedArrivalStop.stopTime = moment(thisLineRide.endDateFormatted).format('HH:mm');
                    }
                    data.travel.totalCost = thisLineRide.price.grossPrice * 100;
                    data.travel.lineRide = thisLineRide;
                } catch (error) {
                    console.error('Error fetching line rides:', error);
                }

                //reload data rows
                this.returnsPriceOfDay = [...this.returnsPriceOfDay];
                this.returnsAll = [...this.returnsAll];
            },

            selectFirstStop(routes) {
                routes.forEach((route) => {
                    route.departureStops = route.lineStopPoints.filter(s => s.stopPointEventList.includes('GETTING_IN'));
                    route.arrivalStops = route.lineStopPoints.filter(s => s.stopPointEventList.includes('GETTING_OUT'));

                    if (route.departureStops.length > 0) {
                        route.selectedDepartureStop = route.departureStops[0];
                    }

                    if (route.arrivalStops.length > 0) {
                        route.selectedArrivalStop = route.arrivalStops[0];
                    }
                });

                return routes;
            },
        },
    };
</script>
<style scoped>
    .search-result {
        margin: 0px;
    }

    .select-button {
        font-size: 1.1rem;
        padding: 8px 2rem;
        margin-top: 8px;
    }

    .stop-name {
        max-width: 85%;
    }
</style>
